import { cva } from "class-variance-authority";
import * as React from "react";
import { Link as ReactLink } from "react-router-dom";

import type { MenuItemProps } from "./types";

import { cn } from "../../../lib/utils";

import { Icon } from "../Icon";

const ICON_SIZE = 20;

const NewBadge = () => (
	<span className="ml-1 inline-flex shrink-0 items-center text-[color:var(--sidenav-fg,currentColor)]">
		<svg
			width="32"
			height="16"
			viewBox="0 0 32 16"
			fill="currentColor"
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label="New feature"
		>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M4 0C1.79086 0 0 1.79086 0 4V12C0 14.2091 1.79086 16 4 16H28C30.2091 16 32 14.2091 32 12V4C32 1.79086 30.2091 0 28 0H4ZM27.1697 5.368L27.3617 9.46H28.2857L28.4777 5.368L28.5257 4H27.1337L27.1697 5.368ZM27.1937 11.92C27.3697 12.096 27.5817 12.184 27.8297 12.184C28.0857 12.184 28.2977 12.096 28.4657 11.92C28.6337 11.744 28.7177 11.52 28.7177 11.248C28.7177 10.976 28.6337 10.752 28.4657 10.576C28.2977 10.4 28.0857 10.312 27.8297 10.312C27.5817 10.312 27.3697 10.4 27.1937 10.576C27.0177 10.752 26.9297 10.976 26.9297 11.248C26.9297 11.52 27.0177 11.744 27.1937 11.92ZM3 4.192V12.04H4.332V8.608C4.332 8.168 4.308 7.72 4.26 7.264C4.22 6.808 4.184 6.372 4.152 5.956H4.2L5.004 7.624L7.452 12.04H8.88V4.192H7.548V7.6C7.548 8.04 7.568 8.496 7.608 8.968C7.656 9.44 7.696 9.876 7.728 10.276H7.68L6.876 8.608L4.428 4.192H3ZM10.875 4.192V12.04H15.723V10.864H12.267V8.548H15.087V7.372H12.267V5.368H15.603V4.192H10.875ZM18.112 12.04L16.552 4.192H17.98L18.664 8.152C18.728 8.568 18.792 8.988 18.856 9.412C18.928 9.828 18.992 10.244 19.048 10.66H19.096C19.184 10.244 19.268 9.828 19.348 9.412C19.436 8.988 19.524 8.568 19.612 8.152L20.572 4.192H21.784L22.744 8.152C22.832 8.56 22.92 8.976 23.008 9.4C23.096 9.816 23.184 10.236 23.272 10.66H23.32C23.384 10.236 23.448 9.816 23.512 9.4C23.576 8.984 23.636 8.568 23.692 8.152L24.376 4.192H25.708L24.208 12.04H22.48L21.508 7.912L21.316 7C21.26 6.696 21.208 6.396 21.16 6.1H21.112C21.056 6.396 20.996 6.696 20.932 7C20.876 7.304 20.816 7.608 20.752 7.912L19.804 12.04H18.112Z"
			/>
		</svg>
	</span>
);

const menuItemVariants = cva(
	[
		"group/menu-item relative flex items-center gap-3 transition-colors",
		"box-border shrink-0",
		"m-0 appearance-none border-0 bg-transparent [font-family:inherit] no-underline",
		"text-[color:var(--sidenav-fg,currentColor)]",
		"focus-visible:ring-2 focus-visible:ring-[color:var(--sidenav-fg,currentColor)] focus-visible:outline-hidden focus-visible:ring-inset",
	],
	{
		variants: {
			variant: {
				link: "mx-2 h-10 rounded-md px-2 text-paragraph-3 font-semibold",
				"nested-link": "mr-2 ml-10 h-10 rounded-md px-2 text-paragraph-3 font-normal",
				category: [
					"mt-3 flex h-6 items-end px-4",
					"text-caption font-normal",
					"text-[color:var(--sidenav-fg-muted,currentColor)]",
					"pointer-events-none cursor-default opacity-70",
				],
			},
			isActive: { true: "", false: "" },
		},
		compoundVariants: [
			{
				variant: "link",
				isActive: false,
				className: "cursor-pointer hover:bg-[color:var(--sidenav-hover-bg,transparent)]",
			},
			{
				variant: "nested-link",
				isActive: false,
				className: "cursor-pointer hover:bg-[color:var(--sidenav-hover-bg,transparent)]",
			},
			{
				variant: "link",
				isActive: true,
				className: "bg-[color:var(--sidenav-active-bg,transparent)]",
			},
			{
				variant: "nested-link",
				isActive: true,
				className: "bg-[color:var(--sidenav-active-bg,transparent)]",
			},
		],
		defaultVariants: {
			variant: "link",
			isActive: false,
		},
	}
);

export const MenuItem = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, MenuItemProps>((props, ref) => {
	const {
		variant = "link",
		label,
		href,
		iconLeft,
		isActive = false,
		onClick,
		fullPageLoad = false,
		isExternal = false,
		badge,
		dangerouslySetClassName,
		"data-testid": dataTestId,
	} = props;

	const isCategory = variant === "category";
	const showIconColumn = variant === "link";
	const className = cn(menuItemVariants({ variant, isActive }), dangerouslySetClassName);

	const content = (
		<>
			{showIconColumn ? (
				<span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
					{iconLeft ? <Icon icon={iconLeft} size={ICON_SIZE} color={null} /> : null}
				</span>
			) : null}
			<span className="min-w-0 flex-1 truncate text-left">{label}</span>
			{badge === "new" ? <NewBadge /> : null}
		</>
	);

	if (isCategory) {
		return (
			<div className={className} role="presentation" data-testid={dataTestId}>
				<span className="min-w-0 flex-1 truncate text-left">{label}</span>
			</div>
		);
	}

	if (href) {
		if (isExternal || fullPageLoad) {
			return (
				<a
					ref={ref as React.Ref<HTMLAnchorElement>}
					href={href}
					className={className}
					onClick={onClick}
					target={isExternal ? "_blank" : undefined}
					rel={isExternal ? "noopener noreferrer" : undefined}
					aria-current={isActive ? "page" : undefined}
					data-testid={dataTestId}
				>
					{content}
				</a>
			);
		}
		return (
			<ReactLink
				ref={ref as React.Ref<HTMLAnchorElement>}
				to={href}
				className={className}
				onClick={onClick}
				aria-current={isActive ? "page" : undefined}
				data-testid={dataTestId}
			>
				{content}
			</ReactLink>
		);
	}

	return (
		<button
			ref={ref as React.Ref<HTMLButtonElement>}
			type="button"
			className={className}
			onClick={onClick}
			aria-current={isActive ? "page" : undefined}
			data-testid={dataTestId}
		>
			{content}
		</button>
	);
});

MenuItem.displayName = "MenuItem";

export { menuItemVariants };
export type { MenuItemProps, MenuItemVariant } from "./types";
